import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";

function UserIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

interface UserAdoptionCardProps {
  activeUserDetails: Record<string, number>;
  todaysActiveUsers: number;
  weekGrowthInPercentage: number;
}

export function UserAdoptionCard({
  activeUserDetails,
  todaysActiveUsers,
  weekGrowthInPercentage,
}: UserAdoptionCardProps) {
  const data = Object.entries(activeUserDetails).map(([day, value]) => ({
    name: day,
    value,
  }));

  const maxYValue = Math.max(...data.map((d) => d.value), 0);
  const roundedMaxY = Math.ceil(maxYValue / 5) * 5 || 5; // Ensure at least 5

  const ticks = Array.from({ length: roundedMaxY }, (_, i) => i + 1);

  const growthDisplay =
    weekGrowthInPercentage >= 0
      ? `+${weekGrowthInPercentage}%`
      : `${weekGrowthInPercentage}%`;
  const growthColor =
    weekGrowthInPercentage >= 0 ? "text-green-500" : "text-red-500";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center pb-2">
        <CardTitle className="text-md font-medium flex items-center">
          <UserIcon className="w-4 h-4 mr-2" /> User Adoption
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground">Daily active users</div>
        <div className="h-[180px] mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis
                dataKey="name"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                domain={[0, roundedMaxY]}
                ticks={ticks}
                allowDecimals={false}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#9b87f5"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Today's Users</div>
            <div className="text-2xl font-medium">{todaysActiveUsers}</div>
          </div>
          <div className={`text-right ${growthColor}`}>
            <div className="text-sm text-muted-foreground">Week Growth</div>
            <div className="text-2xl font-medium">{growthDisplay}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
